'use client';

import { useEffect, useState } from 'react';
import { ChevronRight, ExternalLink, Users } from 'lucide-react';
import { SheetPage } from '@church/nextjs-ui/components/ResponsiveSheet';
import { GroupCard } from '@church/nextjs-ui/website';
import type { FinderGroup, MyGroup } from '@/lib/types';
import { isUnauthorized } from '@/lib/api';
import { useProfile } from './ProfileContext';
import { AvatarStack } from './Avatar';
import { GroupRow } from '@church/nextjs-ui/website/profile';
import { Bar, CARD, CTA_TEAL, FactCard, LINK, PageBody, ProfileSheet } from './ui';

/**
 * The visitor's Life Groups as one card on their details: each group as a
 * row (the faces, the name, whether they lead it). Tap anywhere and the
 * Groups sheet opens with the finder's own cards for each group; the finder
 * is the way to more.
 */
export function GroupsCard() {
  const { api, urls } = useProfile();
  const [catalogue, setCatalogue] = useState<{ groups: FinderGroup[]; detailsUrl: string } | null>(
    null
  );
  const [mine, setMine] = useState<MyGroup[] | null | 'error'>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getGroupCatalogue(controller.signal)
      .then((c) => {
        if (!controller.signal.aborted) setCatalogue(c);
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setCatalogue({ groups: [], detailsUrl: `${urls.groupFinder}details/?id=` });
      });
    api
      .getMyGroups(controller.signal)
      .then((g) => {
        if (!controller.signal.aborted) setMine(g);
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setMine(isUnauthorized(err) ? [] : 'error');
      });
    return () => controller.abort();
  }, [api, urls.groupFinder]);

  const loading = mine === null || catalogue === null;
  const groups = mine === 'error' ? [] : (mine ?? []);
  const shown = groups.slice(0, 3);
  const more = groups.length - shown.length;

  return (
    <>
      <FactCard
        title="Groups"
        icon={<Users className="h-4 w-4" strokeWidth={2.5} />}
        onEdit={() => setOpen(true)}
        editLabel="Open"
      >
        {loading ? (
          <div className="flex flex-col gap-3" aria-busy="true">
            <Bar className="h-9 w-full rounded-2xl" />
            <Bar className="h-9 w-3/4 rounded-2xl" />
          </div>
        ) : mine === 'error' ? (
          <p className="text-sm text-gray-500">We couldn't load your groups right now.</p>
        ) : groups.length === 0 ? (
          <div className="flex flex-col gap-1">
            <p className="text-sm font-bold text-[#1c2b39]">You're not in a Life Group yet</p>
            <p className="text-sm text-gray-600">
              Groups meet across every campus, most weeks, in living rooms and coffee shops.
            </p>
          </div>
        ) : (
          <div className="-mx-2 flex flex-col divide-y divide-gray-100">
            {shown.map((g) => (
              <GroupRow
                key={g.id}
                name={g.name}
                members={g.members}
                badge={g.role === 'leader' ? 'You lead' : "You're in"}
                leader={g.role === 'leader'}
              />
            ))}
            {more > 0 && (
              <p className="px-2 pt-2.5 text-xs text-gray-500">
                and {more} more {more === 1 ? 'group' : 'groups'}
              </p>
            )}
          </div>
        )}
        <a
          href={urls.groupFinder}
          onClick={(e) => e.stopPropagation()}
          className={`${LINK} mt-auto inline-flex items-center gap-1 self-start`}
        >
          Find a group
          <ChevronRight className="h-3.5 w-3.5" strokeWidth={2.5} />
        </a>
      </FactCard>

      <ProfileSheet open={open} onClose={() => setOpen(false)} title="Groups" maxWidth="max-w-2xl">
        <SheetPage name="main">
          <PageBody>
            {groups.length === 0 ? (
              <p className="text-sm text-gray-600">
                You're not in a Life Group yet. Groups meet across every campus, most weeks, in
                living rooms and coffee shops.
              </p>
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {groups.map((g) => {
                  const listed = catalogue?.groups.find((c) => c.id === g.id);
                  return listed && catalogue ? (
                    // With `onOpen` the card shows "Open in Group Connect" for a
                    // member (link mode falls through to Inquire); the card itself
                    // goes to the group's page.
                    <GroupCard
                      key={g.id}
                      group={listed}
                      detailsUrl={catalogue.detailsUrl}
                      mine={g}
                      onOpen={(grp) => window.location.assign(`${catalogue.detailsUrl}${grp.id}`)}
                    />
                  ) : (
                    <UnlistedGroup key={g.id} group={g} />
                  );
                })}
              </div>
            )}
            <a href={urls.groupFinder} className={`${CTA_TEAL} self-start`}>
              Find a group
              <ChevronRight className="h-4 w-4" strokeWidth={2.5} />
            </a>
          </PageBody>
        </SheetPage>
      </ProfileSheet>
    </>
  );
}

/** A group the visitor is in that the public finder does not list (closed, hidden). */
function UnlistedGroup({ group }: { group: MyGroup }) {
  return (
    <article className={`${CARD} flex w-full flex-col gap-4 p-5 ring-[#299c8f]/30 sm:p-6`}>
      <div className="flex items-start justify-between gap-3">
        <h3 className="font-display text-2xl leading-none tracking-wide text-[#1c2b39] uppercase sm:text-3xl">
          {group.name}
        </h3>
        <span className="inline-flex shrink-0 items-center rounded-full bg-[#299c8f] px-2.5 py-1 text-xs font-bold tracking-wide text-white uppercase">
          {group.role === 'leader' ? 'You lead' : "You're in"}
        </span>
      </div>
      {group.members.length > 0 && (
        <div className="flex items-center gap-2">
          <AvatarStack people={group.members} size={28} />
          <span className="text-sm text-gray-600">
            {group.members.length} {group.members.length === 1 ? 'person' : 'people'}
          </span>
        </div>
      )}
      <a
        href={group.connectUrl}
        target="_blank"
        rel="noopener"
        className="mt-auto inline-flex w-full items-center justify-center gap-1 rounded-full bg-[#1c2b39] px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-all hover:scale-105 hover:shadow-md active:scale-95"
      >
        Open in Group Connect
        <ExternalLink className="h-4 w-4" strokeWidth={2.5} />
      </a>
    </article>
  );
}
